import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@/gql";
import type { NotificationPrefCategory, NotificationPrefChannel } from "@/gql/graphql";

const MY_NOTIFICATION_PREFERENCES = gql(`
  query MyNotificationPreferences {
    myNotificationPreferences {
      category
      push
      email
    }
  }
`);

const UPDATE_MY_NOTIFICATION_PREFERENCE = gql(`
  mutation UpdateMyNotificationPreference($input: UpdateNotificationPreferenceInput!) {
    updateMyNotificationPreference(input: $input) {
      category
      push
      email
    }
  }
`);

export interface CategoryPreference {
  category: NotificationPrefCategory;
  push: boolean;
  email: boolean;
}

/**
 * Account-level notification preferences for the current user (push & email per category).
 */
export function useNotificationPreferences() {
  const { data, loading, error } = useQuery(MY_NOTIFICATION_PREFERENCES);

  const [mutate, { loading: saving }] = useMutation(UPDATE_MY_NOTIFICATION_PREFERENCE, {
    // Keep the query cache in sync with the full list the mutation returns.
    update(cache, { data: result }) {
      if (!result?.updateMyNotificationPreference) return;
      cache.writeQuery({
        query: MY_NOTIFICATION_PREFERENCES,
        data: { myNotificationPreferences: result.updateMyNotificationPreference },
      });
    },
  });

  const setPreference = (
    category: NotificationPrefCategory,
    channel: NotificationPrefChannel,
    enabled: boolean
  ) =>
    mutate({
      variables: { input: { category, channel, enabled } },
    });

  return {
    preferences: data?.myNotificationPreferences ?? [],
    loading,
    saving,
    error,
    setPreference,
  };
}
